import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, FileText, Presentation, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { MissingBadge, MISSING_MARK_CLASS } from "./missingContent";

const CONTACT_ID = "contact";

export const scrollToInspectionForm = () => {
  document.getElementById(CONTACT_ID)?.scrollIntoView({ behavior: "smooth" });
};

/* ── Trust stat bar directly under the hero ── */
const STATS: { label: string; value: string }[] = [
  { label: "Years of experience", value: "[CONTENT MISSING: years of experience]" },
  { label: "Inspections supported", value: "[CONTENT MISSING: # inspections supported]" },
  { label: "Global reach", value: "[CONTENT MISSING: # countries / global reach]" },
];

export const InspectionStatBar = () => (
  <section className="relative bg-secondary">
    <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative -mt-8 grid divide-y divide-border/60 rounded-2xl border border-border/50 bg-background shadow-lg sm:grid-cols-3 sm:divide-x sm:divide-y-0"
      >
        {STATS.map((stat, i) => (
          <motion.li
            key={stat.label}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
            className="flex flex-col items-center gap-3 px-6 py-8 text-center"
          >
            <mark className={`text-base sm:text-lg font-bold leading-snug ${MISSING_MARK_CLASS}`}>
              {stat.value}
            </mark>
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {stat.label}
            </span>
          </motion.li>
        ))}
      </motion.ul>
    </div>
  </section>
);

/* ── Sticky header CTA ── */
export const InspectionStickyCta = ({ label }: { label: string }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 520);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={`fixed top-[84px] right-4 sm:right-6 z-40 transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 -translate-y-2"
      }`}
    >
      <Button
        size="sm"
        onClick={scrollToInspectionForm}
        className="group rounded-full pl-4 pr-5 h-10 shadow-xl ring-1 ring-primary-foreground/10 bg-primary text-primary-foreground hover:bg-primary/90"
      >
        <ShieldCheck size={15} className="mr-2 opacity-80" />
        {label}
        <ArrowRight size={14} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
      </Button>
    </div>
  );
};

/* ── Final, page-specific CTA with HubSpot form slot ── */
interface InspectionFinalCtaProps {
  hubspotFormId?: string | null;
}

export const InspectionFinalCta = ({ hubspotFormId }: InspectionFinalCtaProps) => (
  <section id={CONTACT_ID} className="scroll-mt-24 relative overflow-hidden bg-primary py-20 sm:py-28">
    <div
      className="pointer-events-none absolute -top-24 -right-16 h-80 w-80 rounded-full bg-primary-foreground/5 blur-3xl"
      aria-hidden="true"
    />
    <div className="container relative mx-auto grid max-w-6xl items-start gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-foreground/80">
          <ShieldCheck className="h-3.5 w-3.5" />
          Inspection readiness
        </span>
        <h2 className="mt-6 text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-[1.1] tracking-tight text-primary-foreground">
          Don't wait for the knock on the door
        </h2>
        <p className="mt-5 max-w-md text-lg leading-relaxed text-primary-foreground/70">
          An inspection should be a normal business day. Make it one.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <WebinarRegistrationForm
          registrationUrl={null}
          title="Inspection Readiness & Remediation"
          hubspotFormId={hubspotFormId}
          heading="Request a mock audit"
          subtitle="Get a former FDA auditor's perspective on your readiness."
        />
      </motion.div>
    </div>
  </section>
);

/* ── Related content placeholders ── */
const RELATED_SLOTS = [
  { label: "Blog", icon: BookOpen },
  { label: "Case study", icon: FileText },
  { label: "Webinar", icon: Presentation },
];

export const InspectionRelatedPlaceholder = () => (
  <section className="py-20 sm:py-28 bg-secondary/50">
    <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-10">
        Latest insights
      </h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {RELATED_SLOTS.map((slot, i) => (
          <motion.div
            key={slot.label}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="flex min-h-[200px] flex-col justify-between rounded-2xl border border-dashed border-border bg-background p-6"
          >
            <div className="flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              <slot.icon className="h-4 w-4" />
              {slot.label}
            </div>
            <MissingBadge text="[CONTENT MISSING: inspection readiness blog/case/webinar content]" />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
